import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Link } from 'react-router-dom';
import { Trash2, Calendar as CalendarIcon, RefreshCw, AlertTriangle, X } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function DeliveriesList({ user, onLogout }) {
  const [deliveries, setDeliveries] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  
  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Filters - default to 'today' for performance
  const [periodFilter, setPeriodFilter] = useState('today');
  const [specificDate, setSpecificDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const [businessSearch, setBusinessSearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');

  // Check if user is Admin
  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    if (dateFrom || dateTo) {
      fetchDeliveriesRange(dateFrom, dateTo);
    } else if (specificDate) {
      fetchDeliveriesByDate(specificDate);
    } else {
      fetchDeliveries(periodFilter);
    }
  }, [periodFilter, specificDate, dateFrom, dateTo]);

  // Clear selection when deliveries change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [deliveries]);

  const fetchDeliveries = async (period) => {
    try {
      setLoading(true);
      const response = await api.get(`/deliveries?period=${period}`);
      const deliveriesData = response.data;
      setDeliveries(deliveriesData);
      await fetchCustomers(deliveriesData);
    } catch (error) {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveriesByDate = async (date) => {
    try {
      setLoading(true);
      const dateStr = date.toISOString().split('T')[0];
      const response = await api.get(`/deliveries?specific_date=${dateStr}`);
      const deliveriesData = response.data;
      setDeliveries(deliveriesData);
      await fetchCustomers(deliveriesData);
    } catch (error) {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveriesRange = async (from, to) => {
    try {
      setLoading(true);
      let url = '/deliveries?period=all';
      const response = await api.get(url);
      let deliveriesData = response.data;
      // Use local date (not UTC slice) for accurate day-boundary filtering
      if (from) {
        deliveriesData = deliveriesData.filter(d => {
          if (!d.delivery_date) return false;
          const localDate = new Date(d.delivery_date).toLocaleDateString('en-CA'); // YYYY-MM-DD
          return localDate >= from;
        });
      }
      if (to) {
        deliveriesData = deliveriesData.filter(d => {
          if (!d.delivery_date) return false;
          const localDate = new Date(d.delivery_date).toLocaleDateString('en-CA');
          return localDate <= to;
        });
      }
      setDeliveries(deliveriesData);
      await fetchCustomers(deliveriesData);
    } catch (error) {
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async (deliveriesData) => {
    const customerIds = [...new Set(deliveriesData.map(d => d.customer_id).filter(Boolean))];
    const customerData = {};
    
    const batchSize = 10;
    for (let i = 0; i < customerIds.length; i += batchSize) {
      const batch = customerIds.slice(i, i + batchSize);
      await Promise.all(batch.map(async (customerId) => {
        try {
          const customerRes = await api.get(`/customers/${customerId}`);
          customerData[customerId] = customerRes.data;
        } catch (err) {
          console.error(`Failed to fetch customer ${customerId}`);
        }
      }));
    }
    
    setCustomers(customerData);
  };

  const handleDateSelect = (date) => {
    setSpecificDate(date);
    setPeriodFilter(''); // Clear period filter when specific date is selected
    setCalendarOpen(false);
  };

  const clearSpecificDate = () => {
    setSpecificDate(null);
    setPeriodFilter('today');
  };

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    setSpecificDate(null);
    setDateFrom('');
    setDateTo('');
  };

  const handleDelete = async (deliveryId, status, payloadStatus) => {
    const standardDeletable = ['Draft', 'Pending', 'Waiting for Driver'];
    const isOverweight = status === 'Overweight' || payloadStatus === 'OVERWEIGHT';
    
    if (isOverweight) {
      if (!isAdmin) {
        toast.error('Only Admin can delete overweight deliveries');
        return;
      }
    } else if (!standardDeletable.includes(status)) {
      toast.error('Cannot delete completed deliveries. Create a revision instead.');
      return;
    }

    const confirmMessage = isOverweight 
      ? 'Are you sure you want to delete this OVERWEIGHT delivery? This action cannot be undone.'
      : 'Are you sure you want to delete this delivery?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await api.delete(`/deliveries/${deliveryId}`);
      toast.success('Delivery deleted successfully');
      if (specificDate) {
        fetchDeliveriesByDate(specificDate);
      } else {
        fetchDeliveries(periodFilter);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete delivery');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('No deliveries selected');
      return;
    }

    const selectedDeliveries = filteredDeliveries.filter(d => selectedIds.has(d.id));
    const nonDeletable = selectedDeliveries.filter(d => !canDelete(d));
    if (nonDeletable.length > 0) {
      toast.error(`${nonDeletable.length} selected delivery(ies) cannot be deleted (completed status)`);
      return;
    }

    const overweightCount = selectedDeliveries.filter(d => 
      d.status === 'Overweight' || d.payload_status === 'OVERWEIGHT'
    ).length;

    let confirmMessage = `Are you sure you want to delete ${selectedIds.size} delivery(ies)?`;
    if (overweightCount > 0) {
      confirmMessage = `Are you sure you want to delete ${selectedIds.size} delivery(ies)? This includes ${overweightCount} OVERWEIGHT delivery(ies). This action cannot be undone.`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const deliveryId of selectedIds) {
      try {
        await api.delete(`/deliveries/${deliveryId}`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setDeleting(false);
    setSelectedIds(new Set());

    if (failCount === 0) {
      toast.success(`Successfully deleted ${successCount} delivery(ies)`);
    } else {
      toast.warning(`Deleted ${successCount} delivery(ies), ${failCount} failed`);
    }

    if (specificDate) {
      fetchDeliveriesByDate(specificDate);
    } else {
      fetchDeliveries(periodFilter);
    }
  };

  const canDelete = (delivery) => {
    const status = delivery.status;
    const payloadStatus = delivery.payload_status;
    const standardDeletable = ['Draft', 'Pending', 'Waiting for Driver'];
    
    if (isAdmin && (status === 'Overweight' || payloadStatus === 'OVERWEIGHT')) {
      return true;
    }
    
    if ((user.role === 'Admin' || user.role === 'Manager') && standardDeletable.includes(status)) {
      return true;
    }
    
    return false;
  };

  const toggleSelect = (deliveryId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(deliveryId)) {
      newSelected.delete(deliveryId);
    } else {
      newSelected.add(deliveryId);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    const deletableDeliveries = filteredDeliveries.filter(d => canDelete(d));
    
    if (selectedIds.size === deletableDeliveries.length && deletableDeliveries.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(deletableDeliveries.map(d => d.id)));
    }
  };

  const filteredDeliveries = deliveries.filter(delivery => {
    if (invoiceSearch && !delivery.invoice_number?.toLowerCase().includes(invoiceSearch.toLowerCase())) {
      return false;
    }
    if (dateSearch && !new Date(delivery.delivery_date).toLocaleDateString('en-GB').includes(dateSearch)) {
      return false;
    }
    const businessName = customers[delivery.customer_id]?.business_name || '';
    if (businessSearch && !businessName.toLowerCase().includes(businessSearch.toLowerCase())) {
      return false;
    }
    if (statusSearch && !delivery.status.toLowerCase().includes(statusSearch.toLowerCase())) {
      return false;
    }
    return true;
  });

  const deletableCount = filteredDeliveries.filter(d => canDelete(d)).length;
  const allDeletableSelected = deletableCount > 0 && selectedIds.size === deletableCount;

  const getPageTitle = () => {
    if (dateFrom || dateTo) {
      const from = dateFrom || '...';
      const to = dateTo || '...';
      return `Deliveries: ${from} to ${to}`;
    }
    if (specificDate) {
      return `Deliveries for ${specificDate.toLocaleDateString('en-GB', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      })}`;
    }
    switch (periodFilter) {
      case 'today': return "Today's Deliveries";
      case 'week': return "Last 7 Days";
      case 'month': return "Last 30 Days";
      case 'year': return "Last Year";
      case 'all': return "All Deliveries";
      default: return "Deliveries";
    }
  };

  const formatDateForButton = (date) => {
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{getPageTitle()}</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-2">
              {loading ? 'Loading...' : `${filteredDeliveries.length} deliveries found`}
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && selectedIds.size > 0 && (
              <Button
                onClick={handleBulkDelete}
                disabled={deleting}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete ({selectedIds.size})
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={() => specificDate ? fetchDeliveriesByDate(specificDate) : fetchDeliveries(periodFilter)}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {isAdmin && selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {selectedIds.size} delivery(ies) selected
              </span>
            </div>
            <Button
              onClick={() => setSelectedIds(new Set())}
              variant="ghost"
              size="sm"
              className="text-blue-600 hover:text-blue-800"
            >
              Clear Selection
            </Button>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                Filter by Date
              </CardTitle>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                {/* Period Dropdown */}
                <Select value={specificDate ? '' : periodFilter} onValueChange={handlePeriodChange}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today (Default)</SelectItem>
                    <SelectItem value="week">Last 7 Days</SelectItem>
                    <SelectItem value="month">Last 30 Days</SelectItem>
                    <SelectItem value="year">Last Year</SelectItem>
                    <SelectItem value="all">All Time</SelectItem>
                  </SelectContent>
                </Select>

                {/* Calendar Date Picker */}
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={specificDate ? "default" : "outline"}
                      className={`w-full sm:w-auto justify-start text-left font-normal ${
                        specificDate ? 'bg-blue-600 text-white hover:bg-blue-700' : ''
                      }`}
                      data-testid="calendar-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {specificDate ? formatDateForButton(specificDate) : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={specificDate}
                      onSelect={handleDateSelect}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {/* Clear Date Button */}
                {specificDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSpecificDate}
                    className="hover:opacity-80"
                    title="Clear date filter"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}

                {/* Date Range: From-To */}
                <div className="flex items-center gap-1">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setSpecificDate(null); setPeriodFilter(''); }}
                    className="h-9 w-36 text-xs"
                    data-testid="deliveries-date-from"
                  />
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>to</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setSpecificDate(null); setPeriodFilter(''); }}
                    className="h-9 w-36 text-xs"
                    data-testid="deliveries-date-to"
                  />
                  {(dateFrom || dateTo) && (
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => { setDateFrom(''); setDateTo(''); setPeriodFilter('today'); }}
                      title="Clear range"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Mobile Search Filters */}
            <div className="lg:hidden mb-4 space-y-2">
              <Input
                placeholder="Search invoice..."
                value={invoiceSearch}
                onChange={(e) => setInvoiceSearch(e.target.value)}
                className="h-9 text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Date..."
                  value={dateSearch}
                  onChange={(e) => setDateSearch(e.target.value)}
                  className="h-9 text-sm"
                />
                <Input
                  placeholder="Business..."
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <Input
                placeholder="Search status..."
                value={statusSearch}
                onChange={(e) => setStatusSearch(e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-4" />
                <p className="text-slate-600">Loading deliveries...</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full" data-testid="deliveries-table">
                    <thead>
                      <tr className="border-b-2 border-slate-300">
                        {isAdmin && (
                          <th className="text-left py-3 px-2 w-10">
                            <Checkbox
                              checked={allDeletableSelected}
                              onCheckedChange={toggleSelectAll}
                              disabled={deletableCount === 0}
                              title={deletableCount === 0 ? "No deletable deliveries" : "Select all deletable"}
                              data-testid="select-all-checkbox"
                            />
                          </th>
                        )}
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Invoice</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Business Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Payload Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Weight (kg)</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Driver</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                      </tr>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {isAdmin && <th className="py-2 px-2"></th>}
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Search invoice..."
                            value={invoiceSearch}
                            onChange={(e) => setInvoiceSearch(e.target.value)}
                            className="h-8 text-xs"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Search date..."
                            value={dateSearch}
                            onChange={(e) => setDateSearch(e.target.value)}
                            className="h-8 text-xs"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Search business..."
                            value={businessSearch}
                            onChange={(e) => setBusinessSearch(e.target.value)}
                            className="h-8 text-xs"
                          />
                        </th>
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Search status..."
                            value={statusSearch}
                            onChange={(e) => setStatusSearch(e.target.value)}
                            className="h-8 text-xs"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeliveries.length === 0 ? (
                        <tr>
                          <td colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-slate-500">
                            {specificDate 
                              ? `No deliveries found for ${specificDate.toLocaleDateString('en-GB')}. Try a different date.`
                              : periodFilter === 'today' 
                                ? 'No deliveries for today. Try selecting a different period or pick a specific date.'
                                : 'No deliveries found'}
                          </td>
                        </tr>
                      ) : (
                        filteredDeliveries.map((delivery) => {
                          const isDeletable = canDelete(delivery);
                          const isSelected = selectedIds.has(delivery.id);
                          
                          return (
                            <tr 
                              key={delivery.id} 
                              className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                                isSelected ? 'bg-blue-50' : ''
                              }`}
                            >
                              {isAdmin && (
                                <td className="py-3 px-2">
                                  {isDeletable && (
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleSelect(delivery.id)}
                                      data-testid={`select-delivery-${delivery.id}`}
                                    />
                                  )}
                                </td>
                              )}
                              <td className="py-3 px-4 font-medium">{delivery.invoice_number || 'N/A'}</td>
                              <td className="py-3 px-4 text-sm">
                                {new Date(delivery.delivery_date).toLocaleDateString('en-GB')}
                              </td>
                              <td className="py-3 px-4 text-sm font-medium text-slate-700">
                                {customers[delivery.customer_id]?.business_name || 'N/A'}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  delivery.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                  delivery.status === 'Waiting for Driver' ? 'bg-amber-100 text-amber-700' :
                                  delivery.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
                                  delivery.status === 'Overweight' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {delivery.status}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className={`w-2 h-2 rounded-full ${
                                    delivery.payload_status === 'ACCEPTABLE' ? 'bg-green-500' : 'bg-red-500'
                                  }`}></div>
                                  <span className={`text-sm ${delivery.payload_status === 'OVERWEIGHT' ? 'text-red-600 font-medium' : ''}`}>
                                    {delivery.payload_status}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-sm">
                                {delivery.total_pallet_weight_kg} / {delivery.vehicle_payload_kg}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`text-sm ${
                                  delivery.driver_accepted ? 'text-green-600 font-medium' : 'text-slate-400'
                                }`}>
                                  {delivery.driver_name || 'Pending'}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Link to={`/deliveries/${delivery.id}`}>
                                    <Button variant="ghost" size="sm" data-testid={`view-delivery-${delivery.id}`}>View</Button>
                                  </Link>
                                  {isDeletable && (
                                    <Button 
                                      type="button"
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDelete(delivery.id, delivery.status, delivery.payload_status);
                                      }}
                                      className={delivery.payload_status === 'OVERWEIGHT' ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : ''}
                                      data-testid={`delete-delivery-${delivery.id}`}
                                      title={delivery.payload_status === 'OVERWEIGHT' ? 'Delete overweight delivery (Admin only)' : 'Delete delivery'}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile/Tablet Card View */}
                <div className="lg:hidden space-y-3">
                  {isAdmin && deletableCount > 0 && (
                    <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <Checkbox
                        checked={allDeletableSelected}
                        onCheckedChange={toggleSelectAll}
                        data-testid="mobile-select-all-checkbox"
                      />
                      <span className="text-sm text-slate-600">
                        Select all deletable ({deletableCount})
                      </span>
                    </div>
                  )}
                  
                  {filteredDeliveries.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      {specificDate 
                        ? `No deliveries found for ${specificDate.toLocaleDateString('en-GB')}. Try a different date.`
                        : periodFilter === 'today' 
                          ? 'No deliveries for today. Try selecting a different period or pick a specific date.'
                          : 'No deliveries found'}
                    </div>
                  ) : (
                    filteredDeliveries.map((delivery) => {
                      const isDeletable = canDelete(delivery);
                      const isSelected = selectedIds.has(delivery.id);
                      
                      return (
                        <Card 
                          key={delivery.id} 
                          className={`border hover:shadow-md transition-shadow ${
                            delivery.payload_status === 'OVERWEIGHT' ? 'border-red-200 bg-red-50/30' : 
                            isSelected ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                          }`}
                        >
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <div className="flex justify-between items-start">
                                <div className="flex items-start gap-2">
                                  {isAdmin && isDeletable && (
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => toggleSelect(delivery.id)}
                                      className="mt-1"
                                      data-testid={`mobile-select-delivery-${delivery.id}`}
                                    />
                                  )}
                                  <div>
                                    <p className="font-medium text-sm text-slate-900">{delivery.invoice_number || 'N/A'}</p>
                                    <p className="text-xs text-slate-600 mt-0.5">
                                      {new Date(delivery.delivery_date).toLocaleDateString('en-GB')}
                                    </p>
                                  </div>
                                </div>
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  delivery.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                  delivery.status === 'Waiting for Driver' ? 'bg-amber-100 text-amber-700' :
                                  delivery.status === 'Approved' ? 'bg-blue-100 text-blue-700' :
                                  delivery.status === 'Overweight' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {delivery.status}
                                </span>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <p className="text-slate-500">Business</p>
                                  <p className="font-medium text-slate-700 mt-0.5">
                                    {customers[delivery.customer_id]?.business_name || 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Driver</p>
                                  <p className={`font-medium mt-0.5 ${
                                    delivery.driver_accepted ? 'text-green-600' : 'text-slate-400'
                                  }`}>
                                    {delivery.driver_name || 'Pending'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Payload</p>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <div className={`w-2 h-2 rounded-full ${
                                      delivery.payload_status === 'ACCEPTABLE' ? 'bg-green-500' : 'bg-red-500'
                                    }`}></div>
                                    <span className={`text-xs font-medium ${delivery.payload_status === 'OVERWEIGHT' ? 'text-red-600' : ''}`}>
                                      {delivery.payload_status}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-slate-500">Weight</p>
                                  <p className="font-medium text-slate-700 mt-0.5">
                                    {delivery.total_pallet_weight_kg} / {delivery.vehicle_payload_kg} kg
                                  </p>
                                </div>
                              </div>

                              <div className="flex gap-2 pt-2 border-t border-slate-100">
                                <Link to={`/deliveries/${delivery.id}`} className="flex-1">
                                  <Button variant="ghost" size="sm" className="w-full" data-testid={`view-delivery-${delivery.id}`}>
                                    View Details
                                  </Button>
                                </Link>
                                {isDeletable && (
                                  <Button 
                                    type="button"
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDelete(delivery.id, delivery.status, delivery.payload_status);
                                    }}
                                    data-testid={`delete-delivery-${delivery.id}`}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title={delivery.payload_status === 'OVERWEIGHT' ? 'Delete overweight delivery (Admin only)' : 'Delete delivery'}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
